import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { formatCurrency, STAGE_DISPLAY_NAMES, formatDateTime } from '../lib/utils';
import { PipelineMomentum } from '../components/dashboard/PipelineMomentum';
import PipelineVelocityTile from '../components/dashboard/PipelineVelocityTile';
import BuildAndSendTelemetryTile from '../components/dashboard/BuildAndSendTelemetryTile';
import MyAIUsageTile from '../components/dashboard/MyAIUsageTile';
import OnboardingChecklist from '../components/onboarding/OnboardingChecklist';
import { ExecutiveClarityBrief } from '../components/sales-intelligence';
import { FullOpportunityScan } from '../components/dashboard/FullOpportunityScan';
import { 
  DollarSign, 
  TrendingUp, 
  Target, 
  Users,
  Briefcase,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  AlertTriangle,
  Clock,
  ChevronRight,
  CalendarDays,
  Timer,
  Activity,
  Zap,
  Radar,
  Building2,
  Sparkles,
  LineChart,
  Globe,
  Star
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const MOMENTUM_STAGE_NAMES = {
  prospecting: "Prospecting",
  strategic_discovery: "Discovery Completed",
  vision_deck_presented: "Vision Deck Presented",
  executive_deck_presented: "Deck Delivered",
  refinement: "Revision Phase",
  production_alignment: "Vendor Alignment",
};

// Premium welcome messages
const WELCOME_MESSAGES = [
  "Your next transformations are taking shape.",
  "The next opportunities are emerging.",
  "Your pipeline is building momentum.",
  "Strategic opportunities await your attention."
];

// CRM Philosophy quotes - shown one per page refresh
// First quote has higher weight (appears more frequently)
const PHILOSOPHY_QUOTES = [
  { text: "Great salespeople deserve tools that make them look as good as they really are.", weight: 3 },
  { text: "Professional sales deserves professional presentation.", weight: 1 },
  { text: "Your ideas deserve to be seen the way you see them.", weight: 1 },
  { text: "Confidence comes from clarity.", weight: 1 },
  { text: "Vision sells.", weight: 1 },
  { text: "When the story is clear, the sale becomes natural.", weight: 1 },
  { text: "Make the proposal moment unforgettable.", weight: 1 },
  { text: "Great deals begin with great communication.", weight: 1 },
  { text: "Every opportunity deserves a stage.", weight: 1 },
  { text: "A powerful vision closes powerful deals.", weight: 1 },
];

// Get weighted random quote
const getWeightedRandomQuote = () => {
  const weightedQuotes = [];
  PHILOSOPHY_QUOTES.forEach(q => {
    for (let i = 0; i < q.weight; i++) {
      weightedQuotes.push(q.text);
    }
  });
  return weightedQuotes[Math.floor(Math.random() * weightedQuotes.length)];
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAdmin, viewingAsUser, effectiveUser } = useAuth();
  const { get, loading } = useApi();
  const [stats, setStats] = useState(null);
  const [repPerformance, setRepPerformance] = useState([]);
  const [followups, setFollowups] = useState(null);
  const [momentumAlerts, setMomentumAlerts] = useState(null);
  const [signals, setSignals] = useState([]);
  const [selectedTimeframe, setSelectedTimeframe] = useState('90');
  const [showOpportunityScan, setShowOpportunityScan] = useState(false);

  const isSalesRep = effectiveUser?.role === 'sales_rep';

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch stats (auto-filtered for sales reps)
        const statsData = await get('/dashboard/stats');
        setStats(statsData);

        // Fetch follow-ups
        const followupData = await get('/dashboard/followups');
        setFollowups(followupData);

        // Fetch momentum alerts
        const momentumData = await get('/dashboard/momentum-check');
        setMomentumAlerts(momentumData);

        // Fetch market signals for highlights
        try {
          const signalData = await get('/market-intelligence/signals');
          setSignals(signalData?.slice(0, 4) || []);
        } catch (e) {
          console.log('Signals not available');
        }

        // Admin-only: rep performance (only when not viewing as another user)
        if ((isAdmin && !viewingAsUser) || effectiveUser?.role === 'executive_viewer') {
          const perfData = await get('/dashboard/rep-performance');
          setRepPerformance(perfData);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      }
    };

    fetchData();
  }, [get, effectiveUser?.role, isAdmin, viewingAsUser]);

  const stageChartData = stats ? Object.entries(stats.stage_counts || {}).map(([stage, count]) => ({
    name: STAGE_DISPLAY_NAMES[stage] || stage,
    value: count,
    amount: stats.stage_values?.[stage] || 0,
  })).filter(item => !['closed_won', 'closed_lost'].includes(item.name.toLowerCase().replace(/[– ]/g, '_'))) : [];

  const propertyTypeData = stats ? Object.entries(stats.property_types || {}).map(([type, data]) => ({
    name: type,
    value: data.count,
    amount: data.value,
  })) : [];

  const COLORS = ['#B89B5E', '#6B7280', '#3B82F6', '#8B5CF6', '#10B981', '#EF4444'];

  // Generate mock trend data for visualization
  const trendData = useMemo(() => {
    const days = parseInt(selectedTimeframe);
    const data = [];
    const baseValue = stats?.total_pipeline_value || 500000;
    const startValue = baseValue * 0.6;
    
    for (let i = 0; i < days; i += Math.ceil(days / 12)) {
      const progress = i / days;
      const value = startValue + (baseValue - startValue) * Math.pow(progress, 0.8) + Math.random() * 50000;
      data.push({
        day: i,
        value: Math.round(value)
      });
    }
    data.push({ day: days, value: baseValue });
    return data;
  }, [stats?.total_pipeline_value, selectedTimeframe]);

  const pipelineGrowth = useMemo(() => {
    if (trendData.length < 2) return 0;
    const start = trendData[0].value;
    const end = trendData[trendData.length - 1].value;
    return Math.round(((end - start) / start) * 100);
  }, [trendData]);

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Get random welcome message
  const welcomeMessage = useMemo(() => {
    return WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
  }, []);

  // Get philosophy quote (only changes on page refresh)
  const philosophyQuote = useMemo(() => {
    return getWeightedRandomQuote();
  }, []);

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* First-time user checklist — auto-hides for existing users and
            disappears permanently once all items are complete or dismissed. */}
        <OnboardingChecklist />

        {/* CRM Philosophy Quote */}
        <div className="relative py-6 sm:py-8">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#B89B5E]/5 to-transparent" />
          <div className="text-center px-4">
            <p className="font-serif text-lg sm:text-xl lg:text-2xl text-[#1A1A1A]/80 italic leading-relaxed max-w-3xl mx-auto">
              "{philosophyQuote}"
            </p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <div className="h-px w-8 bg-[#B89B5E]/40" />
              <span className="text-xs tracking-widest text-[#B89B5E]/60 uppercase">DH Design Philosophy</span>
              <div className="h-px w-8 bg-[#B89B5E]/40" />
            </div>
          </div>
        </div>

        {/* Full Opportunity Scan CTA */}
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-[#1A1A1A] via-[#2D2D2D] to-[#1A1A1A] border border-[#B89B5E]/20 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNCODlCNUUiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 bg-[#B89B5E]/20 rounded-full animate-ping" />
                <div className="relative p-3 rounded-full bg-gradient-to-br from-[#B89B5E] to-[#8B7355]">
                  <Radar className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold text-white flex items-center gap-2">
                  Run Full Opportunity Scan
                  <Badge className="bg-[#B89B5E] text-white text-xs">AI</Badge>
                </h3>
                <p className="text-sm text-gray-400 mt-0.5">
                  Identify your next best deals, risks, and actions in seconds
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setShowOpportunityScan(true)}
              className="bg-[#B89B5E] hover:bg-[#A68B4E] text-white px-6 py-5 text-base shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
              data-testid="full-scan-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Start Scan
            </Button>
          </div>
        </div>

        {/* Premium Header */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#B89B5E]/5 to-transparent rounded-xl -z-10" />
          <div className="py-4 sm:py-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#B89B5E] animate-pulse" />
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground">
                {getGreeting()}, {effectiveUser?.name?.split(' ')[0] || user?.name?.split(' ')[0]}
              </p>
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#1A1A1A] mb-2">
              {welcomeMessage}
            </h1>
            {stats?.is_filtered_view && (
              <Badge variant="secondary" className="rounded-sm mt-2">
                Showing your opportunities only
              </Badge>
            )}
          </div>
        </div>

        {/* Strategic Command Strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {/* Opportunity Value in Motion */}
          <Card className="bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] border-0 shadow-lg hover:shadow-xl transition-all duration-300" data-testid="stat-pipeline-value">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-[#B89B5E]/20">
                  <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-[#B89B5E]" />
                </div>
                <Badge className="bg-[#B89B5E] text-white text-xs">Live</Badge>
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-white mb-1">
                {stats ? formatCurrency(stats.total_pipeline_value) : '—'}
              </div>
              <p className="text-xs sm:text-sm text-gray-400">
                Opportunity Value in Motion
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {stats?.total_opportunities || 0} active opportunities
              </p>
            </CardContent>
          </Card>

          {/* Transformations In Progress */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:border-[#B89B5E]/50 hover:shadow-lg transition-all duration-300" data-testid="stat-active-opps">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <Activity className="h-4 w-4 text-blue-500 animate-pulse" />
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-[#1A1A1A] mb-1">
                {stats?.active_opportunities ?? '—'}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Transformations in Progress
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Active engagements
              </p>
            </CardContent>
          </Card>

          {/* Conversion Performance */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:border-[#B89B5E]/50 hover:shadow-lg transition-all duration-300" data-testid="stat-win-rate">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600" />
                </div>
                {stats?.win_rate >= 50 ? (
                  <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500" />
                )}
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-[#1A1A1A] mb-1 flex items-baseline gap-1">
                {stats?.win_rate || 0}
                <span className="text-lg">%</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Conversion Performance
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Won vs closed
              </p>
            </CardContent>
          </Card>

          {/* Closing This Month */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:border-[#B89B5E]/50 hover:shadow-lg transition-all duration-300" data-testid="stat-closing-month">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <CalendarDays className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                </div>
                <Star className="h-4 w-4 text-purple-400" />
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-[#1A1A1A] mb-1">
                {stats?.deals_closing_this_month ?? '—'}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Closing This Month
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Expected closes
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline Momentum Section - Enhanced */}
        <PipelineMomentum />

        {/* Pipeline Velocity tile - May 9, 2026 */}
        <PipelineVelocityTile />

        {/* Document 2 · Phase 6.4 — Build & Send Telemetry KPI tile */}
        <BuildAndSendTelemetryTile />

        {/* Document 2 · Phase 9.3 — Rep AI Usage self-service tile */}
        <MyAIUsageTile />

        {/* Executive Clarity Brief */}
        <ExecutiveClarityBrief />

        {/* Market Intelligence Highlights */}
        {signals.length > 0 && (
          <Card className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] border-0 shadow-lg">
            <CardHeader className="pb-2 sm:pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <CardTitle className="text-base sm:text-lg font-light text-white flex items-center gap-2">
                    <Radar className="w-5 h-5 text-[#B89B5E]" />
                    Market Intelligence Highlights
                  </CardTitle>
                  <p className="text-xs sm:text-sm text-gray-400 mt-1">
                    Signals shaping your next opportunities
                  </p>
                </div>
                <Button 
                  asChild 
                  variant="outline" 
                  size="sm" 
                  className="border-white/20 text-white hover:bg-white/10 w-fit"
                >
                  <Link to="/market-intelligence">
                    View All Signals
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pb-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {signals.map((signal, index) => (
                  <Link 
                    key={signal.id || index}
                    to={`/market-intelligence?signal=${signal.id}`}
                    className="p-3 sm:p-4 rounded-lg bg-white/5 backdrop-blur border border-white/10 hover:border-[#B89B5E]/50 transition-colors cursor-pointer group block"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[#B89B5E]/20 shrink-0">
                        <Building2 className="w-4 h-4 text-[#B89B5E]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {signal.property_name || signal.company_name || signal.title?.split(' - ')[0] || 'Signal'}
                        </p>
                        <p className="text-xs text-[#B89B5E] mt-0.5 capitalize">
                          {(signal.signal_type || signal.type || 'market')?.replace(/_/g, ' ')} signal detected
                        </p>
                        {(signal.location?.city || signal.city) && (
                          <p className="text-xs text-gray-500 mt-1">{signal.location?.city || signal.city}</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-[#B89B5E] transition-colors shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Follow-ups & Momentum Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* My Follow-ups */}
          <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-sm hover:shadow-md transition-shadow" data-testid="followups-section">
            <CardHeader className="pb-4 border-b border-[#E8E6E1]">
              <CardTitle className="text-base sm:text-lg font-light flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#B89B5E]" />
                {isSalesRep ? 'My Follow-ups' : 'Follow-ups'}
                {followups?.total_count > 0 && (
                  <Badge className="bg-[#B89B5E] text-white rounded-full ml-2">
                    {followups.total_count}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-4">
              {/* Overdue */}
              {followups?.overdue?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1 uppercase tracking-wider">
                    <AlertTriangle className="h-3 w-3" />
                    Overdue ({followups.overdue.length})
                  </h4>
                  <div className="space-y-2">
                    {followups.overdue.slice(0, 5).map((item) => (
                      <Link
                        key={item.id}
                        to={item.url}
                        className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group border border-red-100"
                        data-testid={`followup-overdue-${item.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium text-red-700">{item.name}</p>
                          <p className="text-xs text-red-600">{item.account_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600">{item.follow_up_date}</span>
                          <ChevronRight className="h-4 w-4 text-red-400 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Due Today */}
              {followups?.due_today?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-amber-600 mb-2 flex items-center gap-1 uppercase tracking-wider">
                    <Clock className="h-3 w-3" />
                    Due Today ({followups.due_today.length})
                  </h4>
                  <div className="space-y-2">
                    {followups.due_today.map((item) => (
                      <Link
                        key={item.id}
                        to={item.url}
                        className="flex items-center justify-between p-3 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors group border border-amber-100"
                        data-testid={`followup-today-${item.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium text-amber-700">{item.name}</p>
                          <p className="text-xs text-amber-600">{item.account_name}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Next 7 Days */}
              {followups?.next_7_days?.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1 uppercase tracking-wider">
                    <Calendar className="h-3 w-3" />
                    Next 7 Days ({followups.next_7_days.length})
                  </h4>
                  <div className="space-y-2">
                    {followups.next_7_days.slice(0, 5).map((item) => (
                      <Link
                        key={item.id}
                        to={item.url}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group"
                        data-testid={`followup-upcoming-${item.id}`}
                      >
                        <div>
                          <p className="text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.account_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{item.follow_up_date}</span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {(!followups || followups.total_count === 0) && (
                <div className="text-center py-8">
                  <Calendar className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground text-sm">No follow-ups scheduled</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Momentum Check Needed */}
          <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-sm hover:shadow-md transition-shadow" data-testid="momentum-section">
            <CardHeader className="pb-4 border-b border-[#E8E6E1]">
              <CardTitle className="text-base sm:text-lg font-light flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Momentum Check Needed
                {momentumAlerts?.total_count > 0 && (
                  <Badge variant="outline" className="rounded-full ml-2 border-amber-300 text-amber-700">
                    {momentumAlerts.total_count}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Stage-aware alerts based on apparel industry sales cycles
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {momentumAlerts?.alerts?.length > 0 ? (
                <div className="space-y-3">
                  {momentumAlerts.alerts.slice(0, 6).map((alert) => (
                    <Link
                      key={alert.id}
                      to={alert.url}
                      className="flex items-center justify-between p-3 bg-amber-50/50 rounded-lg hover:bg-amber-50 transition-colors group border border-amber-200/50"
                      data-testid={`momentum-alert-${alert.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium truncate">{alert.company_name}</p>
                          <Badge variant="outline" className="rounded-full text-xs shrink-0 border-amber-300">
                            {MOMENTUM_STAGE_NAMES[alert.stage] || alert.stage}
                          </Badge>
                        </div>
                        <p className="text-xs text-amber-700">
                          {alert.days_since_activity} days since activity
                        </p>
                      </div>
                      <div className="text-right shrink-0 ml-3">
                        <p className="text-sm font-medium text-[#B89B5E]">{formatCurrency(alert.deal_value)}</p>
                        <ChevronRight className="h-4 w-4 text-amber-400 group-hover:translate-x-1 transition-transform ml-auto" />
                      </div>
                    </Link>
                  ))}
                  
                  {momentumAlerts.total_count > 6 && (
                    <Button 
                      variant="outline" 
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                      onClick={() => navigate('/pipeline')}
                    >
                      View All {momentumAlerts.total_count} Alerts
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-10 w-10 mx-auto mb-3 text-emerald-500" />
                  <p className="text-emerald-600 font-medium">Momentum healthy</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    No alerts at this time
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Pipeline by Stage */}
          <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-sm" data-testid="chart-pipeline-stage">
            <CardHeader className="border-b border-[#E8E6E1]">
              <CardTitle className="text-base sm:text-lg font-light">Pipeline by Stage</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {stageChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stageChartData} layout="vertical">
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip 
                      formatter={(value, name) => [value, 'Count']}
                      contentStyle={{ 
                        backgroundColor: '#1A1A1A', 
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white'
                      }}
                    />
                    <Bar dataKey="value" fill="#B89B5E" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <BarChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No data available</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Property Type */}
          <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-sm" data-testid="chart-property-type">
            <CardHeader className="border-b border-[#E8E6E1]">
              <CardTitle className="text-base sm:text-lg font-light">By Property Type</CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {propertyTypeData.length > 0 ? (
                <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
                  <ResponsiveContainer width="100%" height={180} className="sm:w-1/2">
                    <PieChart>
                      <Pie
                        data={propertyTypeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {propertyTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1A1A1A', 
                          border: 'none',
                          borderRadius: '8px',
                          color: 'white'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2 w-full sm:w-auto">
                    {propertyTypeData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-sm">{item.name}</span>
                        </div>
                        <span className="text-muted-foreground font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[180px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <PieChart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No data available</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Rep Performance (Admin/Executive only) */}
        {(isAdmin || user?.role === 'executive_viewer') && repPerformance.length > 0 && (
          <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-sm" data-testid="rep-performance-table">
            <CardHeader className="border-b border-[#E8E6E1]">
              <CardTitle className="text-base sm:text-lg font-light flex items-center gap-2">
                <Users className="h-5 w-5 text-[#B89B5E]" />
                Team Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="overflow-x-auto">
                <table className="table-luxury">
                  <thead>
                    <tr>
                      <th>Rep Name</th>
                      <th>Opportunities</th>
                      <th>Total Value</th>
                      <th>Won</th>
                      <th>Lost</th>
                      <th>Win Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repPerformance.map((rep) => (
                      <tr key={rep.id}>
                        <td className="font-medium">{rep.name}</td>
                        <td>{rep.total_opportunities}</td>
                        <td className="text-[#B89B5E]">{formatCurrency(rep.total_value)}</td>
                        <td className="text-emerald-600">{rep.closed_won}</td>
                        <td className="text-red-500">{rep.closed_lost}</td>
                        <td>
                          <span className={`${rep.win_rate >= 50 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {rep.win_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-5 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 transition-all"
          >
            <Link to="/territories" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                <Globe className="w-5 h-5 text-[#B89B5E]" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Territories</span>
                <span className="text-xs text-muted-foreground">Strategic intelligence</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
          
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-5 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 transition-all"
          >
            <Link to="/vision-decks" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Vision Decks</span>
                <span className="text-xs text-muted-foreground">Create proposals</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
          
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-5 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5 transition-all"
          >
            <Link to="/pipeline" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Style Opportunities</span>
                <span className="text-xs text-muted-foreground">View pipeline</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
        </div>
      </div>

      {/* Full Opportunity Scan Modal */}
      <FullOpportunityScan 
        isOpen={showOpportunityScan} 
        onClose={() => setShowOpportunityScan(false)} 
      />
    </Layout>
  );
}
